import { Box, Stack, Tooltip } from '@mui/material'
import { forwardRef, type ReactNode } from 'react'

export type SignalLevelProps = {
  rating: number
  scale: string[]
  filledColour?: string
  tooltipTitle?: ReactNode
  size?: 'sm' | 'md' | 'lg'
} & (
  | {
      /** What the meter measures, e.g. "Confidence"; the current scale step is announced as its value. */
      label: string
    }
  | {
      /** For a placeholder, such as a loading skeleton, whose value would be misleading. */
      'aria-hidden': true
    }
)

export const SignalLevel = forwardRef<HTMLDivElement, SignalLevelProps>(function SignalLevel(props, ref) {
  const { rating, scale, filledColour = 'bg.neutral.default', tooltipTitle, size = 'md' } = props
  const accessibleName = 'label' in props ? { 'aria-label': props.label } : { 'aria-hidden': true }
  const cappedRating = Math.max(0, Math.min(rating, scale.length - 1))
  const height = { sm: '12px', md: '18px', lg: '24px' }[size]
  const barWidth = { sm: 2, md: 3, lg: 4 }[size]
  const gap = { sm: 0.5, md: 1, lg: 1 }[size]

  return (
    <Tooltip title={tooltipTitle ?? scale[cappedRating]}>
      <Stack
        role="meter"
        aria-valuenow={cappedRating}
        aria-valuemin={0}
        aria-valuemax={scale.length - 1}
        aria-valuetext={scale[cappedRating]}
        direction="row"
        display="inline-flex"
        gap={gap}
        ref={ref}
        sx={{ height }}
        // Tooltip names its child by the title, which is the meter's value; the child's own aria-label wins.
        {...accessibleName}
      >
        {scale.slice(1).map((_, i) => (
          <Box
            key={i}
            sx={{
              borderRadius: 8,
              width: barWidth,
              backgroundColor: i < cappedRating ? filledColour : 'bg.neutral.muted',
            }}
          />
        ))}
      </Stack>
    </Tooltip>
  )
})
